import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { adminService } from '@/services/adminService'

interface TargetUser {
  id: string
  name: string
  email: string
  role: string
}

interface OriginalAdmin {
  id: string
  name: string
  email: string
}

interface ImpersonationSession {
  active: boolean
  targetUser: TargetUser
  admin: OriginalAdmin
}

interface ImpersonationContextType {
  isImpersonating: boolean
  targetUser: TargetUser | null
  originalAdmin: OriginalAdmin | null
  startImpersonation: (targetUserId: string) => Promise<void>
  endImpersonation: () => Promise<void>
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined)

interface ImpersonationProviderProps {
  children: ReactNode
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null)
  const [originalAdmin, setOriginalAdmin] = useState<OriginalAdmin | null>(null)

  // On mount, check for an active impersonation session and restore state
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await adminService.getImpersonationSession()
        const session = response.data as ImpersonationSession | null

        if (session && session.active) {
          setIsImpersonating(true)
          setTargetUser({
            id: session.targetUser.id,
            name: session.targetUser.name,
            email: session.targetUser.email,
            role: session.targetUser.role,
          })
          setOriginalAdmin({
            id: session.admin.id,
            name: session.admin.name,
            email: session.admin.email,
          })
        }
      } catch {
        // No active session or not authorized — silently ignore
      }
    }

    checkSession()
  }, [])

  const startImpersonation = useCallback(async (targetUserId: string) => {
    const response = await adminService.startImpersonation(targetUserId)
    const session = response.data as ImpersonationSession

    setIsImpersonating(true)
    setTargetUser({
      id: session.targetUser.id,
      name: session.targetUser.name,
      email: session.targetUser.email,
      role: session.targetUser.role,
    })
    setOriginalAdmin({
      id: session.admin.id,
      name: session.admin.name,
      email: session.admin.email,
    })
  }, [])

  const endImpersonation = useCallback(async () => {
    await adminService.endImpersonation()
    setIsImpersonating(false)
    setTargetUser(null)
    setOriginalAdmin(null)
  }, [])

  const value: ImpersonationContextType = {
    isImpersonating,
    targetUser,
    originalAdmin,
    startImpersonation,
    endImpersonation,
  }

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext)
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
